// Imports
import { faAt, faPhone } from '@fortawesome/pro-regular-svg-icons';
import styled from 'styled-components/macro';
import DateTimeCell from '../../../../../../shareables/foundation/front-end/components/tables/date-time-cell';
import { GetBoardMembersResponse } from '../../../../../../shareables/types/api/main/website/page/type/203/board-members/get';
import formatPhoneNumber from '../../../../../../shareables/utils/format-phone-number';
import ContactInfo, { ContactDetails } from '../shared-components/contact-info';
import ProfilePhoto from '../../../../shared-components/profile-photo';
import { useAppSelector } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import UserAvatar from '../../../../shared-components/user-avatar';
import SignInAnchor from '../../../../sign-in-anchor';


// Styled components
const BoardMember = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	
	& > div > div {
		margin-bottom: -0.1875rem;
	}
	
	@media only screen and (max-width: 31.25rem) {
		img {
			display: none;
		}
	}
`;

const BoardMemberRole = styled.span`
	em {
		line-height: 1.2;
	}
	
	padding-top: 0.11428em;
	margin-bottom: 0.11428em !important;
	display: block;
	
	font-size: smaller;
`;

const ProfileAvatar = styled(UserAvatar)`
	display: flex;
	align-items: center;
	justify-content: center;
	
	height: 3.125rem;
	width: 3.125rem;
	font-size: 1.5rem;
	
	box-shadow: 0 0 0.25rem rgba(0, 0, 0, 0.1);
	
	@media only screen and (max-width: 31.25rem) {
		display: none;
	}
`;


// Define the accepted props
interface Props {
	boardMember: GetBoardMembersResponse[0];
}


// Function component
const BoardMembersRow: React.FC<Props> = ({ boardMember }) => {
	// Use Redux functionality
	const website = useAppSelector((state) => state.self.website);
	
	
	// Return JSX
	return (
		<tr>
			<td>
				<BoardMember>
					{boardMember.profilePhotoURL ? (
						<ProfilePhoto size='3.125rem' alt='Profile photo' src={boardMember.profilePhotoURL} />
					) : (
						<ProfileAvatar
							aria-label='Profile photo'
							firstName={boardMember.firstName}
							lastName={boardMember.lastName}
							round
						/>
					)}
					
					<div>
						<div>
							{boardMember.firstName} {boardMember.lastName}
						</div>
						<BoardMemberRole className='is-gray'>
							<em>{boardMember.title}</em>
						</BoardMemberRole>
					</div>
				</BoardMember>
			</td>
			
			{boardMember.termEnds ? (
				<DateTimeCell date={boardMember.termEnds} style={{ whiteSpace: 'nowrap' }} />
			) : (
				<td>N/A</td>
			)}
			
			<td>
				{!boardMember.email && !boardMember.number && !boardMember.missingReason && <em>None</em>}
				
				{boardMember.missingReason === 'UNAUTHENTICATED' && (
					<em>
						<SignInAnchor>Sign in</SignInAnchor> to view
					</em>
				)}
				{boardMember.missingReason === 'PENDING_APPROVAL' && <em>Account must be approved to view</em>}
				<ContactInfo
					contactDetails={[
						boardMember.number
							? {
									icon: faPhone,
									href: `tel:${boardMember.number}`,
									text: formatPhoneNumber(boardMember.number, website?.country.callingCode ?? 1),
								}
							: null,
						boardMember.email
							? {
									icon: faAt,
									href: `mailto:${boardMember.email}`,
									text: boardMember.email,
								}
							: null,
					].filter((contact): contact is ContactDetails => contact !== null)}
				/>
			</td>
		</tr>
	);
};

export default BoardMembersRow;
